import React from "react";
import { Mail, Phone, User } from "lucide-react";

/**
 * Generic governing-body grid used on every public-site /about page.
 * If `members` is empty, renders nothing — no visual regression on sites
 * whose institute admin hasn't added entries yet.
 *
 * Props
 *   members      Array of governing-body member objects from the public API.
 *   theme        Visual variant: "amber" (default poly/mitest), "saffron", "navy", "cream", "clay", "cyan", "emerald".
 *   testidPrefix Per-site test-id prefix (e.g. "poly-gb").
 *   heading      Section heading text. Default "Governing Body".
 *   eyebrow      Section eyebrow text. Default "Board of Governors".
 */
export default function GoverningBodySection({
  members = [],
  theme = "amber",
  testidPrefix = "gb",
  heading = "Governing Body",
  eyebrow = "Board of Governors",
}) {
  if (!Array.isArray(members) || members.length === 0) return null;

  // Theme tokens — keep palette aligned with each public site's visual identity.
  const THEMES = {
    amber: { accent: "#D48F1A", border: "border-amber-200", chip: "bg-amber-50 text-amber-900", ring: "ring-amber-100" },
    navy: { accent: "#D4A017", border: "border-slate-200", chip: "bg-[#001F3F]/5 text-[#001F3F]", ring: "ring-slate-100" },
    cream: { accent: "#AF734A", border: "border-[#E5D9C6]", chip: "bg-[#FBF6EE] text-[#AF734A]", ring: "ring-[#E5D9C6]" },
    saffron: { accent: "#FF7B00", border: "border-[#F0E2C0]", chip: "bg-[#FFF1DC] text-[#8B1A1A]", ring: "ring-[#F0E2C0]" },
    cyan: { accent: "#06B6D4", border: "border-cyan-200", chip: "bg-cyan-50 text-cyan-900", ring: "ring-cyan-100" },
    emerald: { accent: "#0FB28A", border: "border-emerald-200", chip: "bg-emerald-50 text-emerald-900", ring: "ring-emerald-100" },
    yellow: { accent: "#FDC700", border: "border-yellow-200", chip: "bg-yellow-50 text-amber-900", ring: "ring-yellow-100" },
    orange: { accent: "#F4751F", border: "border-orange-200", chip: "bg-orange-50 text-orange-900", ring: "ring-orange-100" },
  };
  const t = THEMES[theme] || THEMES.amber;

  return (
    <section className="mt-12" data-testid={`${testidPrefix}-section`}>
      <div className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-2" style={{ color: t.accent }}>
        {eyebrow}
      </div>
      <h2 className="font-serif text-3xl md:text-4xl text-slate-900 leading-tight">{heading}</h2>
      <div className="mt-2 h-[3px] w-16" style={{ background: t.accent }} />
      <div
        className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        data-testid={`${testidPrefix}-grid`}
      >
        {members.map((m) => (
          <div
            key={m.id}
            data-testid={`${testidPrefix}-card-${m.id}`}
            className={`bg-white border ${t.border} rounded-lg p-5 flex flex-col ring-1 ${t.ring}`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-16 h-16 rounded-full bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center text-slate-400 ring-2`} style={{ "--tw-ring-color": t.accent }}>
                {m.photo_url ? (
                  <img src={m.photo_url} alt={m.full_name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <User size={24} strokeWidth={1.5} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif text-lg text-slate-900 leading-tight">{m.full_name}</div>
                {m.designation && (
                  <div className={`mt-1 inline-block text-[10px] tracking-[0.18em] uppercase font-semibold px-2 py-0.5 rounded-sm ${t.chip}`}>
                    {m.designation}
                  </div>
                )}
                {m.current_profession && (
                  <div className="mt-1.5 text-xs text-slate-600 line-clamp-2">{m.current_profession}</div>
                )}
              </div>
            </div>
            {(m.email || m.mobile_no) && (
              <div className="mt-3 pt-3 border-t border-dashed border-slate-200 space-y-1.5 text-xs text-slate-600">
                {m.email && (
                  <a
                    href={`mailto:${m.email}`}
                    className="inline-flex items-center gap-1.5 hover:text-slate-900 break-all"
                  >
                    <Mail size={12} strokeWidth={1.5} style={{ color: t.accent }} /> {m.email}
                  </a>
                )}
                {m.mobile_no && (
                  <a
                    href={`tel:${m.mobile_no.replace(/\s+/g, "")}`}
                    className="block hover:text-slate-900"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Phone size={12} strokeWidth={1.5} style={{ color: t.accent }} /> {m.mobile_no}
                    </span>
                  </a>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
